// Template-based rubric definitions.
// No AI / API calls — all data is local for fast, reliable generation.

export const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
  "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming",
];

export const SUBJECTS = [
  "Math",
  "Reading",
  "Writing",
  "Science",
  "Social Studies",
  "Art",
  "Music",
  "Physical Education",
  "Technology",
  "General Classroom",
];

export const GRADES = [
  "Kindergarten", "1st Grade", "2nd Grade", "3rd Grade", "4th Grade",
  "5th Grade", "6th Grade", "7th Grade", "8th Grade", "9th Grade",
  "10th Grade", "11th Grade", "12th Grade",
];

export const PERFORMANCE_LEVELS = [
  "Excellent",
  "Good",
  "Satisfactory",
  "Needs Improvement",
];

// Each subject maps to an ordered list of criteria.
// Each criterion has a level→descriptor map.
const D = {
  // Writing
  "Organization": {
    "Excellent": "Clear structure with a strong introduction, well-developed body, and effective conclusion.",
    "Good": "Logical organization; most ideas flow smoothly.",
    "Satisfactory": "Some structure present; ideas may not always connect.",
    "Needs Improvement": "Lacks clear organization; difficult to follow.",
  },
  "Ideas and Content": {
    "Excellent": "Original, insightful ideas supported by strong details and examples.",
    "Good": "Clear main ideas with relevant supporting details.",
    "Satisfactory": "Main ideas present but support is limited or uneven.",
    "Needs Improvement": "Ideas are unclear or missing supporting details.",
  },
  "Grammar and Mechanics": {
    "Excellent": "Free of grammar, spelling, and punctuation errors.",
    "Good": "Minor errors that do not interfere with meaning.",
    "Satisfactory": "Several errors but meaning is generally clear.",
    "Needs Improvement": "Frequent errors make the writing hard to understand.",
  },
  // Math
  "Accuracy": {
    "Excellent": "All answers and steps are correct.",
    "Good": "Most answers correct with only minor errors.",
    "Satisfactory": "Some correct work with noticeable errors.",
    "Needs Improvement": "Many errors; work shows misunderstanding.",
  },
  "Problem-Solving Process": {
    "Excellent": "Uses an efficient strategy and shows all work clearly.",
    "Good": "Uses an appropriate strategy with most work shown.",
    "Satisfactory": "Attempts a strategy but steps are incomplete.",
    "Needs Improvement": "Little or no strategy shown; work is missing.",
  },
  "Explanation of Thinking": {
    "Excellent": "Clearly explains reasoning using accurate vocabulary.",
    "Good": "Explanation is mostly clear with appropriate vocabulary.",
    "Satisfactory": "Some explanation given but lacks detail or clarity.",
    "Needs Improvement": "Little or no explanation of thinking.",
  },
  // Science
  "Understanding of Concept": {
    "Excellent": "Demonstrates deep understanding and applies concepts accurately.",
    "Good": "Shows solid understanding of key concepts.",
    "Satisfactory": "Shows partial understanding of the concept.",
    "Needs Improvement": "Shows limited understanding of the concept.",
  },
  "Evidence and Explanation": {
    "Excellent": "Strong evidence supports clear, detailed explanations.",
    "Good": "Relevant evidence supports a clear explanation.",
    "Satisfactory": "Some evidence used; explanation needs more detail.",
    "Needs Improvement": "Little to no evidence used; explanation is unclear.",
  },
  // Social Studies
  "Understanding of Topic": {
    "Excellent": "Demonstrates thorough understanding of the topic and its context.",
    "Good": "Shows clear understanding of the topic.",
    "Satisfactory": "Shows basic understanding; some gaps remain.",
    "Needs Improvement": "Demonstrates limited understanding of the topic.",
  },
  "Use of Details": {
    "Excellent": "Uses specific, accurate details to support all points.",
    "Good": "Uses relevant details to support most points.",
    "Satisfactory": "Some details used; not all support the topic.",
    "Needs Improvement": "Few or no supporting details provided.",
  },
  // General
  "Understanding": {
    "Excellent": "Demonstrates a strong understanding of the assignment expectations.",
    "Good": "Shows clear understanding of the assignment.",
    "Satisfactory": "Shows partial understanding of the assignment.",
    "Needs Improvement": "Shows little understanding of the assignment.",
  },
  "Quality of Work": {
    "Excellent": "Work is neat, polished, and reflects careful attention to detail.",
    "Good": "Work is well-done with minor imperfections.",
    "Satisfactory": "Work is acceptable but could be more polished.",
    "Needs Improvement": "Work is incomplete or shows little care.",
  },
  // Reading
  "Comprehension": {
    "Excellent": "Demonstrates a deep understanding of the text and its meaning.",
    "Good": "Shows solid understanding of the main idea and details.",
    "Satisfactory": "Shows basic comprehension with some misunderstandings.",
    "Needs Improvement": "Shows limited comprehension of the text.",
  },
  "Vocabulary and Fluency": {
    "Excellent": "Uses strong vocabulary and reads fluently with expression.",
    "Good": "Uses appropriate vocabulary and reads with good fluency.",
    "Satisfactory": "Vocabulary and fluency are developing.",
    "Needs Improvement": "Vocabulary and fluency need significant practice.",
  },
  "Analysis and Response": {
    "Excellent": "Provides insightful analysis with strong supporting evidence.",
    "Good": "Provides clear analysis with relevant evidence.",
    "Satisfactory": "Some analysis offered but lacks depth.",
    "Needs Improvement": "Little or no analysis of the text.",
  },
  // Art
  "Creativity and Originality": {
    "Excellent": "Highly creative work that shows original thinking.",
    "Good": "Creative work with original elements.",
    "Satisfactory": "Some creativity shown; ideas are familiar.",
    "Needs Improvement": "Limited creativity or originality.",
  },
  "Technique and Skill": {
    "Excellent": "Excellent use of materials and techniques; high skill demonstrated.",
    "Good": "Solid use of techniques and materials.",
    "Satisfactory": "Developing technique; some skills emerging.",
    "Needs Improvement": "Limited technique or skill demonstrated.",
  },
  // Music
  "Performance Skill": {
    "Excellent": "Performs with accuracy, expression, and confidence.",
    "Good": "Performs accurately with good expression.",
    "Satisfactory": "Performs with developing accuracy and expression.",
    "Needs Improvement": "Needs significant practice to perform accurately.",
  },
  "Musical Technique": {
    "Excellent": "Demonstrates strong technique, rhythm, and tone.",
    "Good": "Shows solid technique, rhythm, and tone.",
    "Satisfactory": "Technique is developing with some inconsistencies.",
    "Needs Improvement": "Technique requires significant development.",
  },
  // PE
  "Skill Execution": {
    "Excellent": "Performs skills correctly and consistently.",
    "Good": "Performs skills correctly most of the time.",
    "Satisfactory": "Performs skills with some inconsistency.",
    "Needs Improvement": "Skill execution needs significant practice.",
  },
  "Participation and Sportsmanship": {
    "Excellent": "Fully engaged; demonstrates excellent sportsmanship and teamwork.",
    "Good": "Engaged and shows positive sportsmanship.",
    "Satisfactory": "Participates but engagement is inconsistent.",
    "Needs Improvement": "Limited participation or sportsmanship.",
  },
  // Technology
  "Skill Application": {
    "Excellent": "Confidently and correctly applies tools and skills.",
    "Good": "Applies tools and skills correctly in most cases.",
    "Satisfactory": "Developing comfort with the tools and skills.",
    "Needs Improvement": "Tools and skills are not yet applied correctly.",
  },
  "Creativity and Problem Solving": {
    "Excellent": "Approaches problems creatively with strong solutions.",
    "Good": "Solves problems with appropriate solutions.",
    "Satisfactory": "Attempts to solve problems; solutions are limited.",
    "Needs Improvement": "Struggles to solve problems independently.",
  },
  // Universal
  "Effort and Completion": {
    "Excellent": "Completed the entire assignment with outstanding effort.",
    "Good": "Completed the assignment with strong effort.",
    "Satisfactory": "Completed most of the assignment with adequate effort.",
    "Needs Improvement": "Assignment is incomplete or shows minimal effort.",
  },
};

export const SUBJECT_CRITERIA = {
  "Writing": ["Organization", "Ideas and Content", "Grammar and Mechanics", "Effort and Completion"],
  "Math": ["Accuracy", "Problem-Solving Process", "Explanation of Thinking", "Effort and Completion"],
  "Science": ["Understanding of Concept", "Evidence and Explanation", "Accuracy", "Effort and Completion"],
  "Social Studies": ["Understanding of Topic", "Use of Details", "Organization", "Effort and Completion"],
  "Reading": ["Comprehension", "Vocabulary and Fluency", "Analysis and Response", "Effort and Completion"],
  "Art": ["Creativity and Originality", "Technique and Skill", "Quality of Work", "Effort and Completion"],
  "Music": ["Performance Skill", "Musical Technique", "Quality of Work", "Effort and Completion"],
  "Physical Education": ["Skill Execution", "Participation and Sportsmanship", "Understanding", "Effort and Completion"],
  "Technology": ["Skill Application", "Creativity and Problem Solving", "Quality of Work", "Effort and Completion"],
  "General Classroom": ["Understanding", "Quality of Work", "Organization", "Effort and Completion"],
};

export function getDescriptor(criterion, level) {
  return D[criterion]?.[level] || `${level} performance for ${criterion.toLowerCase()}.`;
}

// Distribute totalPoints across the criteria. Each criterion has 4 levels valued
// at 100%, 75%, 50%, 25% of its share (rounded to nearest integer, never below 0).
export function buildRubric({ state, subject, grade, topic, totalPoints }) {
  const criteria = SUBJECT_CRITERIA[subject] || SUBJECT_CRITERIA["General Classroom"];
  const total = Math.max(1, parseInt(totalPoints, 10) || 20);
  const perCriterion = total / criteria.length;
  const pctByLevel = { "Excellent": 1, "Good": 0.75, "Satisfactory": 0.5, "Needs Improvement": 0.25 };

  const rows = criteria.map((c) => ({
    criterion: c,
    levels: PERFORMANCE_LEVELS.map((lvl) => ({
      name: lvl,
      points: Math.max(0, Math.round(perCriterion * pctByLevel[lvl])),
      descriptor: getDescriptor(c, lvl),
    })),
  }));

  return {
    title: topic?.trim() || `${subject} Rubric`,
    state, subject, grade,
    totalPoints: total,
    perCriterion: Math.round(perCriterion),
    levels: PERFORMANCE_LEVELS,
    rows,
    generatedAt: new Date().toISOString(),
  };
}

export function rubricToPlainText(rubric) {
  const lines = [];
  lines.push(rubric.title.toUpperCase());
  lines.push("=".repeat(rubric.title.length));
  const metaParts = [];
  if (rubric.state) metaParts.push(`State: ${rubric.state}`);
  metaParts.push(`Subject: ${rubric.subject}`);
  metaParts.push(`Grade: ${rubric.grade}`);
  lines.push(metaParts.join("   "));
  lines.push(`Total Points: ${rubric.totalPoints}`);
  lines.push("");
  rubric.rows.forEach((row) => {
    lines.push(`-- ${row.criterion} --`);
    row.levels.forEach((lvl) => {
      lines.push(`  ${lvl.name} (${lvl.points} pts): ${lvl.descriptor}`);
    });
    lines.push("");
  });
  lines.push("Student Name: __________________________   Score: ______ / " + rubric.totalPoints);
  return lines.join("\n");
}
